import { Box, Chip, Stack, Typography } from '@mui/material';
import { FileText } from 'lucide-react';
import type { MouseEvent } from 'react';
import type { FileUploadUploadedItem } from './types';

export interface FileUploadUploadedChipsProps {
  label?: string;
  items: FileUploadUploadedItem[];
  onRemove?: (id: number) => void;
}

/**
 * Thin chip list for documents already linked to a response (below the dashed upload shell).
 */
export function FileUploadUploadedChips({ label, items, onRemove }: FileUploadUploadedChipsProps) {
  if (items.length === 0) return null;

  return (
    <Stack spacing={1} sx={{ width: '100%' }} data-testid='file-upload-uploaded-chips'>
      {label ? (
        <Typography variant='caption' fontWeight={700} color='text.secondary' component='div'>
          {label}
        </Typography>
      ) : null}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.75,
          alignItems: 'center',
        }}
      >
        {items.map((item) => {
          const removable = Boolean(onRemove);
          const href = item.downloadUrl ?? undefined;
          const linkAsChip = Boolean(href && !removable);

          return (
            <Chip
              key={item.id}
              data-testid={`file-upload-uploaded-chip-${item.id}`}
              size='small'
              variant='action'
              icon={<FileText size={14} strokeWidth={2} aria-hidden />}
              label={item.name}
              onDelete={removable ? () => onRemove?.(item.id) : undefined}
              component={linkAsChip ? 'a' : 'div'}
              href={linkAsChip ? (href as string) : undefined}
              clickable={linkAsChip}
              target={linkAsChip ? '_blank' : undefined}
              rel={linkAsChip ? 'noopener noreferrer' : undefined}
              onMouseDown={removable ? (ev: MouseEvent) => ev.stopPropagation() : undefined}
              sx={{
                maxWidth: '100%',
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
              }}
            />
          );
        })}
      </Box>
    </Stack>
  );
}
